import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Chip,
  useTheme,
  alpha,
  Button,
} from '@mui/material';
import {
  TravelExplore as TravelExploreIcon,
  MyLocation as MyTripsIcon,
  Add as AddIcon,
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Business as BusinessIcon,
  Home as HomeIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import AddPropertyButton from '../user/AddPropertyButton';
import { PropertyContext } from '../../contexts/PropertyContext';
import { getUserPartners } from '../../services/partnerService';

const UserSidebar = ({ showMobileLogo = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { t } = useTranslation(['common', 'general', 'user']);
  const { setSelectedProperty } = useContext(PropertyContext);
  const { accounts, instance } = useMsal();
  
  const [userHasProperties, setUserHasProperties] = useState(false);
  
  // Get user roles from the account
  const currentAccount = accounts[0];
  const userRoles = currentAccount?.idTokenClaims?.extension_Role ? 
    currentAccount.idTokenClaims.extension_Role.split(',').map(role => role.trim()) : [];
  
  const isAdmin = userRoles.includes('Admin');
  const isPartnerAdmin = userRoles.includes('PartnerAdmin');

  // Check if user has properties
  useEffect(() => {
    const checkUserProperties = async () => {
      if (!isAdmin && !isPartnerAdmin) {
        try {
          const partners = await getUserPartners(instance);
          setUserHasProperties(partners && partners.length > 0);
        } catch (error) {
          console.error('Error checking user properties:', error);
        }
      }
    };

    checkUserProperties();
  }, [instance, isAdmin, isPartnerAdmin]);

  const isActiveRoute = (path) => {
    return location.pathname === path;
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  // User-focused navigation items (available to all authenticated users)
  const userNavItems = [
    {
      id: 'home',
      label: t('navigation.home', { ns: 'common' }),
      icon: <HomeIcon />,
      path: '/',
      dividerAfter: false,
    },
    {
      id: 'guides',
      label: t('navigation.guides', { ns: 'common' }),
      icon: <TravelExploreIcon />,
      path: '/guides',
      dividerAfter: false,
    },
    {
      id: 'myTrips',
      label: t('header.nav.myTrips', { ns: 'common' }),
      icon: <MyTripsIcon />,
      path: '/my-trips',
      dividerAfter: true,
    },
  ];

  // Admin navigation items (only for admins and partner admins)
  const adminNavItems = [];
  
  if (isPartnerAdmin || isAdmin) {
    adminNavItems.push({
      id: 'dashboard',
      label: t('navigation.dashboard', { ns: 'common' }),
      icon: <DashboardIcon />,
      path: isAdmin ? '/adminDashboard' : '/partnerDashboard',
      dividerAfter: false,
    });
  }

  if (isAdmin) {
    adminNavItems.push({
      id: 'userManagement',
      label: t('navigation.userManagement', { ns: 'common' }),
      icon: <AdminIcon />,
      path: '/userManagement',
      dividerAfter: false,
    });
  }

  // Account navigation items (available to all)
  const accountNavItems = [
    {
      id: 'profile',
      label: t('navigation.profile', { ns: 'common' }),
      icon: <PersonIcon />,
      path: '/profile',
      dividerAfter: false,
    },
  ];

  const renderNavItems = (items, sectionTitle = null) => {
    if (items.length === 0) return null;

    return (
      <Box>
        {sectionTitle && (
          <Typography
            variant="caption"
            sx={{
              px: 2,
              py: 1,
              display: 'block',
              color: 'text.secondary',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 1,
            }}
          >
            {sectionTitle}
          </Typography>
        )}
        <List disablePadding>
          {items.map((item) => (
            <React.Fragment key={item.id}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleNavigation(item.path)}
                  selected={isActiveRoute(item.path)}
                  sx={{
                    mx: 1,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      '& .MuiListItemIcon-root': {
                        color: theme.palette.primary.main,
                      },
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.15),
                      },
                    },
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.action.hover, 0.5),
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 40,
                      color: isActiveRoute(item.path) ? 'primary.main' : 'inherit',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: '0.9rem',
                      fontWeight: isActiveRoute(item.path) ? 600 : 400,
                    }}
                  />
                </ListItemButton>
              </ListItem>
              {item.dividerAfter && <Divider sx={{ my: 1 }} />}
            </React.Fragment>
          ))}
        </List>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
      }}
    >
      {/* Mobile Logo */}
      {showMobileLogo && (
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            WorldExplore
          </Typography>
        </Box>
      )}

      {/* User Role Indicator */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {isAdmin && (
            <Chip
              size="small"
              label="Admin"
              color="error"
              variant="outlined"
              icon={<AdminIcon />}
            />
          )}
          {isPartnerAdmin && (
            <Chip
              size="small"
              label="Partner Admin"
              color="warning"
              variant="outlined"
              icon={<BusinessIcon />}
            />
          )}
          <Chip
            size="small"
            label="User"
            color="primary"
            variant="outlined"
            icon={<PersonIcon />}
          />
        </Box>
      </Box>

      {/* Navigation Sections */}
      <Box sx={{ flex: 1, overflow: 'auto', py: 1 }}>
        {/* Core User Navigation */}
        {renderNavItems(userNavItems, "Navigation")}
        
        {/* Add Property Section for Regular Users and Admin */}
        {!isPartnerAdmin && (
          <Box sx={{ px: 2, py: 1 }}>
            <Typography
              variant="caption"
              sx={{
                py: 1,
                display: 'block',
                color: 'text.secondary',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: 1,
              }}
            >
              {t('properties.title', { ns: 'user' })}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: 'text.secondary',
                fontSize: '0.75rem',
                mb: 1,
                fontStyle: 'italic'
              }}
            >
              {t('properties.sectionDescription', { ns: 'user' })}
            </Typography>
            
            {/* Show property management button if user has properties */}
            {userHasProperties && (
              <Button
                variant="contained"
                size="small"
                fullWidth
                startIcon={<SettingsIcon />}
                onClick={() => navigate('/partnerDashboard')}
                sx={{ mb: 1 }}
              >
                {t('properties.manageProperties', { ns: 'user' })}
              </Button>
            )}
            
            <AddPropertyButton 
              variant="outlined" 
              size="small" 
              fullWidth 
              onPropertyCreated={async (result) => {
                // result expected: { PropertyId, PropertyName, Status, Message }
                if (result?.propertyId || result?.PropertyId) {
                  const newId = result.PropertyId || result.propertyId;
                  // Persist selection
                  localStorage.setItem('selectedProperty', newId);
                  setSelectedProperty(newId);
                  setUserHasProperties(true);
                  // Mark onboarding just-created flag for potential snackbar usage elsewhere
                  sessionStorage.setItem('justCreatedProperty', 'true');
                  
                  try {
                    // Force token refresh to get updated roles (PartnerAdmin was just assigned)
                    const account = instance.getActiveAccount();
                    if (account) {
                      await instance.acquireTokenSilent({
                        scopes: ['openid', 'profile', 'email'],
                        account: account,
                        forceRefresh: true
                      });
                    }
                  } catch (error) {
                    console.error('Error refreshing token after property creation:', error);
                  }
                  
                  // Reload the page to refresh MSAL authentication state with new PartnerAdmin role
                  window.location.href = '/partnerDashboard';
                } else {
                  // Fallback if response shape unexpected
                  navigate('/partnerDashboard');
                }
              }}
            />
          </Box>
        )}
        
        {/* Admin Navigation */}
        {adminNavItems.length > 0 && renderNavItems(adminNavItems, "Administration")}
        
        {/* Account Navigation */}
        {renderNavItems(accountNavItems, "Account")}
      </Box>

      {/* Quick Action Button */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => handleNavigation('/guides')}
            sx={{
              borderRadius: 1,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
            }}
          >
            <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
              <AddIcon />
            </ListItemIcon>
            <ListItemText
              primary={t('navigation.createGuide', { ns: 'common' })}
              primaryTypographyProps={{
                fontSize: '0.9rem',
                fontWeight: 600,
              }}
            />
          </ListItemButton>
        </ListItem>
      </Box>
    </Box>
  );
};

export default UserSidebar;
